import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Typography,
  Divider,
  List,
  Tooltip,
  Space,
  Tag,
  Select,
  Checkbox,
  InputNumber,
  Modal,
  message,
  Empty,
  Badge,
  Button,
} from "antd";
import { useBookingWizard } from "../../contexts/BookingWizardContext";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useTariffs, Tariff } from "../../hooks/useTariffs";
import { useTransportTypes } from "../../hooks/useTransportTypes";
import { useIncoterms } from "../../hooks/useIncoterms";
import { useCompanySuppliers } from "../../hooks/useCompanySuppliers";
import { useCompany } from "../../hooks/useCompany";
import dayjs from "dayjs";
import { useProductForm } from "../../contexts/ProductFormContext";
import { supabase } from "../../lib/supabase";
import { getEnv } from "../../lib/env";
import {
  CUSTOMS_CLEARANCE,
  HANDLING,
  DELIVERY,
  AIR_FREIGHT,
  IMPORT_DUTIES_NOTE,
  PICKUP_FEE,
  EXPORT_CLEARANCE_LICENSE,
} from "../../lib/pricingDefaults";

const { Title, Text } = Typography;

// Helper function to format euro values with 2 decimal places
const formatEuro = (value: number) => `€ ${value.toFixed(2)}`;

// Helper function to format weights with 2 decimal places
const formatWeight = (value: number) => `${value.toFixed(2)} kg`;

// Feature flag to control whether to use product form context
const ENABLE_PRODUCT_FORM =
  getEnv("VITE_ENABLE_PRODUCT_FORM", "false") === "true";

// Custom hook to safely handle conditional ProductForm usage
const useSafeProductForm = () => {
  // Default values when provider is missing
  const defaultValues = { currentFormValues: null, isEditingProduct: false };

  // Always call the hook to satisfy React's rules
  try {
    // Try to use the form context, and if it fails, use default values
    return useProductForm();
  } catch (err) {
    // If feature flag is enabled, log the error
    if (ENABLE_PRODUCT_FORM) {
      console.error(
        "ProductFormProvider missing but feature flag is enabled:",
        err
      );
    }
    // Return default values
    return defaultValues;
  }
};

// Define the CalculatedPrices type with the new pickupFee field
interface CalculatedPrices {
  pricePerKg: number;
  volumeWeight: number;
  chargeableWeight: number;
  transportCost: number;
  customsClearance: number;
  handlingFees: number;
  deliveryCost: number;
  totalCost: number;
  totalDeliveryCost: number;
  dieselSurcharge: number;
  pickupFee: number;
  exportClearanceLicense: number;
}

// Define the PickupRequest type
interface PickupRequest {
  id: number;
  booking_id: number;
  status: "requested" | "pending" | "offer" | "accepted" | "expired";
  booking_blob: {
    booking_id: number;
    reference?: string;
    supplier?: {
      name: string;
      address: string;
      city: string;
      country: string;
      province?: string;
      phone?: string;
      email?: string;
    };
    products_summary: {
      total_cbm: number;
      total_cartons: number;
      total_weight: number;
      total_pieces: number;
    };
    transport_info: {
      transport_type: string;
      incoterm: string;
      origin_pickup_date?: string;
    };
  };
  created_at: string;
  cost?: number;
  price?: number;
  accepted_at?: string;
  offer_at?: string;
  notes?: string;
}

/**
 * A component that displays an estimated price calculation for the current booking
 * This provides a detailed breakdown of costs based on the booking information
 */
export const PricingCalculator: React.FC = () => {
  // Add CSS for the pulse animation
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse {
        0% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
        100% {
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { state, dispatch } = useBookingWizard();
  const { t } = useTranslation();
  const { tariffs } = useTariffs();
  const { transportTypes } = useTransportTypes();
  const { incoterms } = useIncoterms();
  const { selectedCompany } = useCompany();
  const { suppliers } = useCompanySuppliers(selectedCompany);

  // Always call the hook, but it will return fallback values if feature is disabled
  const { currentFormValues, isEditingProduct } = useSafeProductForm();

  // State for selected ports
  const [selectedOriginPortId, setSelectedOriginPortId] = useState<
    number | null
  >(null);
  const [selectedDestinationPortId, setSelectedDestinationPortId] = useState<
    number | null
  >(null);
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);

  // State to store booking supplier information
  const [bookingSupplier, setBookingSupplier] = useState<{
    id: number;
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
    province: string;
  } | null>(null);

  // Fetch the booking supplier if we have a supplier_id
  useEffect(() => {
    if (state.supplier_id) {
      const fetchBookingSupplier = async () => {
        try {
          const { data, error } = await supabase
            .from("booking_supplier")
            .select("*")
            .eq("id", state.supplier_id)
            .single();

          if (error) {
            console.error("Error fetching booking supplier:", error);
            return;
          }

          if (data) {
            setBookingSupplier(data);
            console.log("Set booking supplier:", data);
          }
        } catch (error) {
          console.error("Error in booking supplier fetch:", error);
        }
      };

      fetchBookingSupplier();
    } else {
      setBookingSupplier(null);
    }
  }, [state.supplier_id]);

  // State for calculated pricing
  const [calculatedPrices, setCalculatedPrices] = useState<CalculatedPrices>({
    pricePerKg: 0,
    volumeWeight: 0,
    chargeableWeight: 0,
    transportCost: 0,
    customsClearance: 0,
    handlingFees: 0,
    deliveryCost: 0,
    totalCost: 0,
    totalDeliveryCost: 0,
    dieselSurcharge: 0,
    pickupFee: 0,
    exportClearanceLicense: 0,
  });

  const [applicableTariff, setApplicableTariff] = useState<Tariff | null>(null);

  // Get the names of transport type and incoterm for display
  const transportType =
    transportTypes.find((t) => t.id === state.transport_type)?.name || "";
  const incoterm =
    incoterms.find((i) => i.id === state.booking_incoterm)?.name || "";

  // Create combined route options for the dropdown
  const routeOptions = useMemo(() => {
    if (!state.transport_type) return [];

    // Get all available tariffs for the current transport type
    const availableTariffs = tariffs.filter((tariff) => {
      // Filter by transport type
      const transportTypeMatches =
        tariff.transport_type.id === state.transport_type;

      // For sea freight, check FCL/LCL based on booking_type
      // In this application, booking_type is likely related to FCL/LCL selection
      if (state.transport_type === 2 && state.booking_type) {
        // Assuming booking_type 1 is FCL and 2 is LCL (adjust if different)
        const isFcl = state.booking_type === 1;

        // Use bookings_type instead of fcl_lcl
        // bookings_type.id = 1 for FCL, bookings_type.id = 2 for LCL
        // (adjust these IDs if your system uses different mappings)
        const expectedBookingsTypeId = isFcl ? 1 : 2;

        return (
          transportTypeMatches &&
          tariff.bookings_type?.id === expectedBookingsTypeId
        );
      }

      return transportTypeMatches;
    });

    // Group tariffs by origin-destination pairs
    const routeGroups: Record<string, Tariff[]> = {};

    availableTariffs.forEach((tariff) => {
      const routeKey = `${tariff.origin_port.id}-${tariff.destination_port.id}`;
      if (!routeGroups[routeKey]) {
        routeGroups[routeKey] = [];
      }
      routeGroups[routeKey].push(tariff);
    });

    // Create options for each unique route
    return Object.keys(routeGroups).map((routeKey) => {
      const tariffs = routeGroups[routeKey];
      const firstTariff = tariffs[0]; // Use the first tariff for route info

      // Get the most recent valid tariff (for price display)
      const validTariffs = tariffs
        .filter((t) => !t.to || dayjs(t.to).isAfter(dayjs()))
        .sort((a, b) => {
          // Sort by validity start date in descending order
          if (!a.from) return 1;
          if (!b.from) return -1;
          return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
        });

      const currentTariff =
        validTariffs.length > 0 ? validTariffs[0] : firstTariff;

      // Create the label with route and price
      let label = `${firstTariff.origin_port.name} → ${firstTariff.destination_port.name}`;

      // Add price information for air freight
      if (state.transport_type === 1 && currentTariff.price) {
        label += ` (€${currentTariff.price.toFixed(2)}/kg)`;
      }
      // Add price for sea freight
      else if (state.transport_type === 2 && currentTariff.price) {
        if (currentTariff.bookings_type?.id === 2) {
          // FCL has ID 2
          label += ` (€${currentTariff.price.toFixed(2)}/container)`;
        } else {
          label += ` (€${currentTariff.price.toFixed(2)}/cbm)`;
        }
      }

      return {
        value: routeKey,
        label,
        originPortId: firstTariff.origin_port.id,
        destinationPortId: firstTariff.destination_port.id,
        tariffId: currentTariff.id,
      };
    });
  }, [state.transport_type, state.booking_type, tariffs]);

  // Add effect to auto-select first route when options change
  useEffect(() => {
    if (
      routeOptions.length > 0 &&
      !selectedOriginPortId &&
      !selectedDestinationPortId
    ) {
      const firstRoute = routeOptions[0];
      if (firstRoute) {
        setSelectedOriginPortId(firstRoute.originPortId);
        setSelectedDestinationPortId(firstRoute.destinationPortId);
        setSelectedTariffId(firstRoute.tariffId);
      }
    }
  }, [routeOptions, selectedOriginPortId, selectedDestinationPortId]);

  // Handle route selection
  const handleRouteChange = (routeValue: string) => {
    const selectedRoute = routeOptions.find(
      (route) => route.value === routeValue
    );

    if (selectedRoute) {
      setSelectedOriginPortId(selectedRoute.originPortId);
      setSelectedDestinationPortId(selectedRoute.destinationPortId);
      setSelectedTariffId(selectedRoute.tariffId);
    }
  };

  // Calculate costs whenever relevant booking details change
  useEffect(() => {
    const calculateCosts = () => {
      // Get products from both the booking state and current editing form
      const storedProducts = state.products || [];

      // Create a collection of all products to calculate with
      let allProducts = [...storedProducts];

      // If user is currently editing a product form, include those values in calculations
      if (isEditingProduct && currentFormValues) {
        // Only include form values if they have at least one measurement filled
        const hasFormValues =
          currentFormValues.gross_weight ||
          currentFormValues.cbm ||
          currentFormValues.value ||
          currentFormValues.pieces ||
          currentFormValues.cartons;

        if (hasFormValues) {
          // Add the current form values as a temporary product
          allProducts = [
            ...storedProducts,
            {
              name: currentFormValues.name || "New Product",
              hs_code: currentFormValues.hs_code || null,
              gross_weight: currentFormValues.gross_weight || 0,
              cbm: currentFormValues.cbm || 0,
              value: currentFormValues.value || 0,
              pieces: currentFormValues.pieces || 0,
              cartons: currentFormValues.cartons || 0,
            },
          ];
        }
      }

      // Get the data needed from the booking
      const transportTypeId = state.transport_type;
      const incotermId = state.booking_incoterm;

      // Collect product data from all products (stored + currently editing)
      const totalWeight = allProducts.reduce(
        (sum, product) => sum + (product.gross_weight || 0),
        0
      );
      const totalCBM = allProducts.reduce(
        (sum, product) => sum + (product.cbm || 0),
        0
      );
      const totalCartons = allProducts.reduce(
        (sum, product) => sum + (product.cartons || 0),
        0
      );

      // Calculate customs clearance fee based on unique HS codes
      // Get all unique HS codes from products
      const uniqueHsCodes = new Set(
        allProducts.map((product) => product.hs_code).filter((hsCode) => hsCode) // Filter out null or undefined HS codes
      );

      // Calculate total customs clearance fee based on unique HS codes
      const uniqueHsCodesCount = uniqueHsCodes.size;
      const clearanceCost =
        CUSTOMS_CLEARANCE.BASE_FEE +
        Math.max(0, uniqueHsCodesCount - CUSTOMS_CLEARANCE.INCLUDED_HS_CODES) *
          CUSTOMS_CLEARANCE.EXTRA_HS_CODE_FEE;

      const handlingCost = HANDLING.BASE_FEE; // Base handling fee
      let transportCost = 0;
      let deliveryCost = 0;
      let pricePerKg = 0;
      let volumeWeight = 0;
      let chargeableWeight = 0;
      let dieselSurcharge = 0;
      let pickupFee = 0;

      // Determine if pickup fee applies based on incoterm (EXW = 1)
      const isEXWIncoterm = incotermId === 1;

      // Only apply pickup fee if it's EXW incoterm and explicitly included
      pickupFee = isEXWIncoterm && state.pickupFee ? state.pickupFee : 0;

      // Find applicable tariff - now use the selected tariff instead of finding it
      const applicableTariff =
        selectedTariffId && tariffs.find((t) => t.id === selectedTariffId);

      if (applicableTariff) {
        // Calculate transport cost based on transport type
        if (transportTypeId === 1) {
          // Air freight
          // For air freight, calculate volumetric weight (1 CBM = 167 kg)
          volumeWeight = totalCBM * AIR_FREIGHT.VOLUMETRIC_FACTOR;
          // Use whichever is higher: actual weight or volumetric weight
          chargeableWeight = Math.max(totalWeight, volumeWeight);
          pricePerKg = applicableTariff.price;
          transportCost = chargeableWeight * pricePerKg;
          // Add diesel surcharge
          dieselSurcharge = transportCost * AIR_FREIGHT.DIESEL_SURCHARGE_RATE;
        } else if (transportTypeId === 2) {
          // Sea freight
          if (applicableTariff.bookings_type?.id === 2) {
            // For FCL, it's a fixed price per container
            transportCost = applicableTariff.price;
          } else {
            // For LCL, it's price per CBM
            transportCost = totalCBM * applicableTariff.price;
          }
        }

        // Calculate delivery cost based on goods volume
        deliveryCost =
          totalCartons > 0
            ? Math.min(
                DELIVERY.MIN_FEE + totalCartons * DELIVERY.PER_CARTON_FEE,
                DELIVERY.MAX_FEE
              )
            : 0;
      } else {
        // No tariff found, use fallback calculation
        transportCost =
          totalWeight > 0 ? totalWeight * AIR_FREIGHT.DEFAULT_PRICE_PER_KG : 0;

        if (transportTypeId === 1) {
          // Air freight only
          volumeWeight = totalCBM * AIR_FREIGHT.VOLUMETRIC_FACTOR;
          chargeableWeight = Math.max(totalWeight, volumeWeight);
          pricePerKg = AIR_FREIGHT.DEFAULT_PRICE_PER_KG;
          transportCost = chargeableWeight * pricePerKg;
          dieselSurcharge = transportCost * AIR_FREIGHT.DIESEL_SURCHARGE_RATE;
        }

        deliveryCost = DELIVERY.DEFAULT_FEE;
      }

      // Determine if export clearance license fee applies (not for FOB = 3)
      const isFOBIncoterm = incotermId === 3;
      const exportClearanceLicenseFee =
        !isFOBIncoterm && state.includeExportClearanceLicense
          ? EXPORT_CLEARANCE_LICENSE.BASE_FEE
          : 0;

      // Calculate total with available fees based on current step visibility
      const totalCost =
        transportCost +
        (state.currentStep >= CUSTOMS_CLEARANCE.DISPLAY_FROM_STEP
          ? clearanceCost
          : 0) +
        (state.currentStep >= HANDLING.DISPLAY_FROM_STEP ? handlingCost : 0) +
        (state.currentStep >= DELIVERY.DISPLAY_FROM_STEP ? deliveryCost : 0) +
        (state.currentStep >= AIR_FREIGHT.DISPLAY_FROM_STEP && dieselSurcharge
          ? dieselSurcharge
          : 0) +
        pickupFee + // Always include pickup fee if applicable
        exportClearanceLicenseFee; // Always include export clearance license fee if applicable

      setCalculatedPrices({
        pricePerKg,
        volumeWeight,
        chargeableWeight,
        transportCost,
        customsClearance: clearanceCost,
        handlingFees: handlingCost,
        deliveryCost,
        totalCost,
        totalDeliveryCost: deliveryCost,
        dieselSurcharge,
        pickupFee,
        exportClearanceLicense: exportClearanceLicenseFee,
      });

      setApplicableTariff(applicableTariff || null);
    };

    calculateCosts();
  }, [
    state,
    state.transport_type,
    state.products,
    state.currentStep,
    state.booking_incoterm,
    state.pickupFee,
    state.includeExportClearanceLicense,
    tariffs,
    isEditingProduct,
    currentFormValues,
    selectedTariffId,
  ]);

  // Get status color for transport type tag
  const getTransportTypeColor = () => {
    if (state.transport_type === 1) return "blue"; // Air
    if (state.transport_type === 2) return "orange"; // Sea
    return "default";
  };

  // Debug supplier information
  console.log("Supplier ID:", state.supplier_id);
  console.log("Available suppliers:", suppliers);
  console.log(
    "Found supplier:",
    selectedTariffId ? tariffs.find((t) => t.id === selectedTariffId) : null
  );

  // Handle pickup fee toggle
  const handlePickupFeeChange = (value: number | null) => {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step: state.currentStep,
        data: {
          pickupFee: value || 0,
        },
      },
    });
  };

  // Add handler for export clearance license fee toggle
  const handleExportClearanceLicenseChange = (checked: boolean) => {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step: state.currentStep,
        data: {
          includeExportClearanceLicense: checked,
        },
      },
    });
  };

  // State for pickup request and history
  const [isPickupRequestModalVisible, setIsPickupRequestModalVisible] =
    useState(false);
  const [isRequestingPickup, setIsRequestingPickup] = useState(false);
  const [pickupRequest, setPickupRequest] = useState<PickupRequest | null>(
    null
  );
  const [pickupRequestHistory, setPickupRequestHistory] = useState<
    PickupRequest[]
  >([]);
  const [isPickupDetailsModalVisible, setIsPickupDetailsModalVisible] =
    useState(false);
  const [isHistoryDetailsModalVisible, setIsHistoryDetailsModalVisible] =
    useState(false);
  const [selectedHistoryRequest, setSelectedHistoryRequest] =
    useState<PickupRequest | null>(null);

  // Add function to fetch the latest pickup request for this booking
  useEffect(() => {
    if (state.id) {
      fetchLatestPickupRequest();
    }
  }, [state.id]);

  // Function to fetch the latest pickup request
  const fetchLatestPickupRequest = async () => {
    if (!state.id) return;

    try {
      const { data, error } = await supabase
        .from("booking_pickup_request")
        .select("*")
        .eq("booking_id", state.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 is the "no rows returned" error
        console.error("Error fetching pickup request:", error);
        return;
      }

      if (data) {
        setPickupRequest(data);
        console.log("Set latest pickup request:", data);

        // If the status is accepted, update the state with the cost
        if (data.status === "accepted" && data.cost) {
          dispatch({
            type: "UPDATE_STEP",
            payload: {
              step: state.currentStep,
              data: {
                pickupFee: data.cost,
              },
            },
          });
        }
      }
    } catch (error) {
      console.error("Error in pickup request fetch:", error);
    }
  };

  // Function to fetch all pickup requests for history
  const fetchAllPickupRequests = async () => {
    if (!state.id) return;

    try {
      const { data, error } = await supabase
        .from("booking_pickup_request")
        .select("*")
        .eq("booking_id", state.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching pickup request history:", error);
        return;
      }

      if (data && data.length > 0) {
        setPickupRequestHistory(data);
        console.log("Set pickup request history:", data);
      } else {
        setPickupRequestHistory([]);
      }
    } catch (error) {
      console.error("Error in pickup request history fetch:", error);
    }
  };

  // Function to show pickup request details and load history
  const showPickupDetails = () => {
    setIsPickupDetailsModalVisible(true);
    fetchAllPickupRequests();
  };

  // Function to request a new pickup fee quote (reused for both new and additional requests)
  const requestNewPickupFeeQuote = async () => {
    // First confirm with the user that requesting a new quote will expire any existing offers
    Modal.confirm({
      title: t("newQuoteConfirmTitle", "Request New Pickup Fee Quote?"),
      content: t(
        "newQuoteConfirmContent",
        "Requesting a new quote will expire all previous offers. You will need to wait for the new quote to be processed before you can confirm it."
      ),
      okText: t("requestNewQuote", "Request New Quote"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        // If there are existing pickup requests for this booking, mark them as expired
        if (
          pickupRequest &&
          (pickupRequest.status === "offer" ||
            pickupRequest.status === "pending")
        ) {
          try {
            // Mark old requests with offer status as expired
            const { error } = await supabase
              .from("booking_pickup_request")
              .update({ status: "expired" })
              .eq("booking_id", state.id)
              .in("status", ["offer", "pending"]);

            if (error) {
              console.error("Error expiring old pickup requests:", error);
              message.error(
                t("expireRequestError", "Failed to expire previous requests")
              );
            } else {
              message.success(
                t("expireRequestSuccess", "Previous offers have been expired")
              );
            }
          } catch (error) {
            console.error("Error in expiring old pickup requests:", error);
          }
        }

        // Close the current details modal and open the request modal
        setIsPickupDetailsModalVisible(false);
        setTimeout(() => {
          openPickupRequestModal();
        }, 300); // Small delay to avoid UI flicker
      },
    });
  };

  // Add function to open the pickup request modal
  const openPickupRequestModal = () => {
    // Only open if we have a booking ID
    if (!state.id) {
      message.error(
        t(
          "pickupRequestNeedsBooking",
          "You need to save the booking first before requesting a pickup fee quote"
        )
      );
      return;
    }
    setIsPickupRequestModalVisible(true);
  };

  // Get status badge color based on request status
  const getStatusColor = (
    status: string
  ): "default" | "processing" | "success" | "error" | "warning" => {
    switch (status) {
      case "requested":
        return "processing";
      case "pending":
        return "warning";
      case "offer":
        return "warning";
      case "accepted":
        return "success";
      case "expired":
        return "default";
      default:
        return "default";
    }
  };

  // Get status text based on request status
  const getStatusText = (status: string): string => {
    switch (status) {
      case "requested":
        return t("pickupRequest.statusRequested");
      case "pending":
        return t("pickupRequest.statusPending");
      case "offer":
        return t("pickupRequest.statusOffer");
      case "accepted":
        return t("pickupRequest.statusAccepted");
      case "expired":
        return t("pickupRequest.statusExpired");
      default:
        return status;
    }
  };

  // Add function to accept a pickup fee offer
  const acceptPickupFeeOffer = async () => {
    if (
      !pickupRequest ||
      pickupRequest.status !== "offer" ||
      !pickupRequest.cost
    ) {
      return;
    }

    try {
      // Update the status to accepted
      const { error: updateError } = await supabase
        .from("booking_pickup_request")
        .update({
          status: "accepted",
          accepted_at: new Date().toISOString(),
        })
        .eq("id", pickupRequest.id);

      if (updateError) throw updateError;

      // Update the pickup fee in the booking state
      dispatch({
        type: "UPDATE_STEP",
        payload: {
          step: state.currentStep,
          data: {
            pickupFee: pickupRequest.cost,
          },
        },
      });

      // Update local state
      setPickupRequest({
        ...pickupRequest,
        status: "accepted",
        accepted_at: new Date().toISOString(),
      });

      message.success(t("pickupOfferAccepted", "Pickup fee offer accepted"));
    } catch (error) {
      console.error("Error accepting pickup fee offer:", error);
      message.error(
        t("pickupOfferAcceptError", "Failed to accept pickup fee offer")
      );
    }
  };

  // Add function to prepare the booking information for the request
  const prepareBookingBlob = () => {
    // Get products information
    const totalCBM =
      state.products?.reduce((sum, product) => sum + (product.cbm || 0), 0) ||
      0;
    const totalCartons =
      state.products?.reduce(
        (sum, product) => sum + (product.cartons || 0),
        0
      ) || 0;
    const totalWeight =
      state.products?.reduce(
        (sum, product) => sum + (product.gross_weight || 0),
        0
      ) || 0;
    const totalPieces =
      state.products?.reduce(
        (sum, product) => sum + (product.pieces || 0),
        0
      ) || 0;

    // Prepare the booking blob with all relevant information
    return {
      booking_id: state.id,
      reference: state.reference,
      supplier: bookingSupplier
        ? {
            name: bookingSupplier.name,
            address: bookingSupplier.address,
            city: bookingSupplier.city,
            country: bookingSupplier.country,
            province: bookingSupplier.province,
            phone: bookingSupplier.phone,
            email: bookingSupplier.email,
          }
        : null,
      products_summary: {
        total_cbm: totalCBM,
        total_cartons: totalCartons,
        total_weight: totalWeight,
        total_pieces: totalPieces,
      },
      transport_info: {
        transport_type: transportType,
        incoterm: incoterm,
        origin_pickup_date: state.origin_pickup_date,
      },
    };
  };

  // Handle new or updated pickup fee request submission
  const handlePickupFeeRequest = async () => {
    try {
      setIsRequestingPickup(true);

      // Prepare the booking information blob
      const bookingBlob = prepareBookingBlob();

      const { error } = await supabase
        .from("booking_pickup_request")
        .insert({
          booking_id: state.id,
          status: "requested",
          booking_blob: bookingBlob,
          notes: null,
          cost: null,
        })
        .select()
        .single();

      if (error) throw error;

      message.success(
        t("pickupRequestSuccess", "Pickup fee request submitted successfully")
      );
      setIsPickupRequestModalVisible(false);

      // Refresh the pickup request data
      fetchLatestPickupRequest();
    } catch (error) {
      console.error("Error submitting pickup request:", error);
      message.error(
        t("pickupRequestError", "Failed to submit pickup fee request")
      );
    } finally {
      setIsRequestingPickup(false);
    }
  };

  // Function to show details of a historical request
  const showHistoryRequestDetails = (request: PickupRequest) => {
    setSelectedHistoryRequest(request);
    setIsHistoryDetailsModalVisible(true);
  };

  // Function to determine if we should show the pickup fee field
  const shouldShowPickupFee = () => {
    return (
      state.currentStep >= PICKUP_FEE.DISPLAY_FROM_STEP &&
      state.booking_incoterm === 1
    );
  };

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Title level={5}>
            {t("bookingWizard.pricingCalculator.title", "Price Estimation")}
          </Title>
          {isEditingProduct && currentFormValues && (
            <Tag color="processing" style={{ animation: "pulse 2s infinite" }}>
              {t("bookingWizard.pricingCalculator.livePreview", "Live Preview")}
            </Tag>
          )}
        </div>
      }
      style={{
        marginBottom: 16,
        width: "100%",
        marginTop: 0,
      }}
      bodyStyle={{ padding: "16px" }}
      className="booking-wizard-pricing-calculator"
    >
      {/* Live Preview Notice */}
      {isEditingProduct && currentFormValues && (
        <div style={{ marginBottom: 16 }}>
          <Text
            type="secondary"
            style={{ fontSize: "12px", fontStyle: "italic" }}
          >
            {t(
              "bookingWizard.pricingCalculator.livePreviewDescription",
              "Showing estimated costs including the product you're currently editing. Values will be updated when product is saved."
            )}
          </Text>
        </div>
      )}

      {/* Booking Summary Section */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>
          {t("bookingWizard.bookingSummary", "Booking Summary")}
        </Text>
        <List size="small" style={{ marginTop: 8 }} itemLayout="horizontal">
          {transportType && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>
                {t(
                  "bookingWizard.transportDetails.transportType",
                  "Transport Type"
                )}
              </Text>
              <Tag color={getTransportTypeColor()}>{transportType}</Tag>
            </List.Item>
          )}

          {incoterm && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>
                {t("bookingWizard.transportDetails.incoterm", "Incoterm")}
              </Text>
              <Tag>{incoterm}</Tag>
            </List.Item>
          )}

          {/* Export Clearance License Option (not for FOB incoterm) */}
          {state.booking_incoterm !== 3 && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Checkbox
                checked={state.includeExportClearanceLicense}
                onChange={(e) =>
                  handleExportClearanceLicenseChange(e.target.checked)
                }
              >
                <span>{t("includeExportClearanceLicense")}</span>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {formatEuro(EXPORT_CLEARANCE_LICENSE.BASE_FEE)}
                </Text>
              </Checkbox>
            </List.Item>
          )}

          {/* Pickup Fee Input (only for EXW incoterm) */}
          {shouldShowPickupFee() && (
            <List.Item
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Space>
                  <Text>{t("pickupFee")}</Text>
                  <Tooltip title={t("pickupFeeTooltip")}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <div>
                  {pickupRequest ? (
                    <Typography.Link
                      onClick={showPickupDetails}
                      style={{
                        fontSize: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Badge
                        status={getStatusColor(pickupRequest.status)}
                        text={getStatusText(pickupRequest.status)}
                        style={{ fontSize: "12px" }}
                      />
                    </Typography.Link>
                  ) : (
                    <Typography.Link
                      onClick={openPickupRequestModal}
                      style={{ fontSize: "12px" }}
                    >
                      <QuestionCircleOutlined style={{ marginRight: 4 }} />
                      {t("requestPickupFeeQuote")}
                    </Typography.Link>
                  )}
                </div>
              </div>
              {/* Show the cost from the pickup request instead of the input field if available */}
              {pickupRequest && pickupRequest.cost ? (
                <Text strong>{formatEuro(pickupRequest.cost)}</Text>
              ) : (
                <InputNumber
                  value={state.pickupFee || 0}
                  onChange={handlePickupFeeChange}
                  min={0}
                  step={1}
                  precision={2}
                  prefix="€"
                  style={{ width: 120 }}
                />
              )}
            </List.Item>
          )}

          {/* Replace separate port selections with single route selection */}
          <List.Item
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Text>{t("bookingWizard.transportDetails.route", "Route")}</Text>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Select
                style={{ width: 280 }}
                value={`${selectedOriginPortId}-${selectedDestinationPortId}`}
                onChange={handleRouteChange}
                placeholder={t(
                  "bookingWizard.transportDetails.selectRoute",
                  "Select route"
                )}
                options={routeOptions}
                disabled={!state.transport_type}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {selectedTariffId &&
                applicableTariff &&
                applicableTariff.from && (
                  <Text
                    type="secondary"
                    style={{ fontSize: "12px", marginTop: "4px" }}
                  >
                    {t(
                      "bookingWizard.pricingCalculator.tariffValidityPeriod",
                      "Valid"
                    )}
                    : {dayjs(applicableTariff.from).format("DD/MM/YYYY")} -{" "}
                    {applicableTariff.to
                      ? dayjs(applicableTariff.to).format("DD/MM/YYYY")
                      : t("common.unlimited")}
                  </Text>
                )}
            </div>
          </List.Item>

          {state.origin_pickup_date && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>
                {t(
                  "bookingWizard.transportDetails.cargoReadinessDate",
                  "Cargo Readiness"
                )}
              </Text>
              <Text>
                {dayjs(state.origin_pickup_date).format("DD/MM/YYYY")}
              </Text>
            </List.Item>
          )}

          {state.products && state.products.length > 0 && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>{t("bookingWizard.productsSummary", "Products")}</Text>
              <Tag color="green">{state.products.length}</Tag>
            </List.Item>
          )}

          {bookingSupplier && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>
                {t(
                  "bookingWizard.transportDetails.supplierLocation",
                  "Supplier Location"
                )}
              </Text>
              <Tag color="geekblue">{`${bookingSupplier.city}, ${bookingSupplier.country}`}</Tag>
            </List.Item>
          )}
        </List>
      </div>

      <Divider />

      {/* ===== TRANSPORT COSTS SECTION ===== */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>
          {t(
            "bookingWizard.pricingCalculator.transportCostsSectionTitle",
            "Transport"
          )}
        </Text>
        <List size="small" itemLayout="horizontal" style={{ marginTop: 8 }}>
          {/* Air freight specific information */}
          {state.transport_type === 1 && (
            <>
              {calculatedPrices.pricePerKg > 0 && (
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Space
                    direction="vertical"
                    size={0}
                    style={{ alignItems: "flex-start" }}
                  >
                    <Space>
                      <Text>
                        {t(
                          "bookingWizard.pricingCalculator.pricePerKilogram",
                          "Price per kilogram"
                        )}
                      </Text>
                      <Tooltip
                        title={t(
                          "bookingWizard.pricingCalculator.pricePerKilogramTooltip",
                          "Base rate per kilogram of freight"
                        )}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                    {applicableTariff && (
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {applicableTariff.origin_port.name} →{" "}
                        {applicableTariff.destination_port.name}
                      </Text>
                    )}
                  </Space>
                  <Text>{formatEuro(calculatedPrices.pricePerKg)}</Text>
                </List.Item>
              )}

              {calculatedPrices.volumeWeight > 0 && (
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Space>
                    <Text>
                      {t(
                        "bookingWizard.pricingCalculator.volumeWeight",
                        "Volume weight"
                      )}
                    </Text>
                    <Tooltip
                      title={t(
                        "bookingWizard.pricingCalculator.volumeWeightTooltip",
                        "The volumetric weight calculated from dimensions (1 CBM = 167 kg)"
                      )}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                  <Text>{formatWeight(calculatedPrices.volumeWeight)}</Text>
                </List.Item>
              )}

              {calculatedPrices.chargeableWeight > 0 && (
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Space>
                    <Text>
                      {t(
                        "bookingWizard.pricingCalculator.chargeableWeight",
                        "Chargeable weight"
                      )}
                    </Text>
                    <Tooltip
                      title={t(
                        "bookingWizard.pricingCalculator.chargeableWeightTooltip",
                        "The higher of actual weight and volume weight. For air freight, we compare the actual gross weight with the volumetric weight (calculated as 1 CBM = 167 kg) and charge based on whichever is higher."
                      )}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                  <Text>{formatWeight(calculatedPrices.chargeableWeight)}</Text>
                </List.Item>
              )}
            </>
          )}

          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Space
              direction="vertical"
              size={0}
              style={{ alignItems: "flex-start" }}
            >
              <Space>
                <Text>
                  {t(
                    "bookingWizard.pricingCalculator.transportCost",
                    "Transport cost"
                  )}
                </Text>
                <Tooltip
                  title={t(
                    "bookingWizard.pricingCalculator.transportCostTooltip",
                    "Base transportation cost"
                  )}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              {applicableTariff && (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {applicableTariff.origin_port.name} →{" "}
                  {applicableTariff.destination_port.name}
                  {applicableTariff.bookings_type &&
                    ` (${applicableTariff.bookings_type.name})`}
                </Text>
              )}
            </Space>
            <Text>{formatEuro(calculatedPrices.transportCost)}</Text>
          </List.Item>

          {/* Add the pickup fee line item in the transport costs section */}
          {calculatedPrices.pickupFee > 0 && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Space>
                <Text>{t("pickupFee")}</Text>
                <Tooltip title={t("pickupFeeTooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              <Text>{formatEuro(calculatedPrices.pickupFee)}</Text>
            </List.Item>
          )}

          {/* Add the export clearance license fee line item in the transport costs section */}
          {calculatedPrices.exportClearanceLicense > 0 && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Space>
                <Text>{t("exportClearanceLicense")}</Text>
                <Tooltip title={t("exportClearanceLicenseTooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              <Text>{formatEuro(calculatedPrices.exportClearanceLicense)}</Text>
            </List.Item>
          )}
        </List>
      </div>

      {/* ===== CUSTOMS & HANDLING SECTION ===== */}
      {state.currentStep >=
        Math.min(
          CUSTOMS_CLEARANCE.DISPLAY_FROM_STEP,
          HANDLING.DISPLAY_FROM_STEP
        ) && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {t(
              "bookingWizard.pricingCalculator.customsHandlingSectionTitle",
              "Customs & Handling"
            )}
          </Text>
          <List size="small" itemLayout="horizontal" style={{ marginTop: 8 }}>
            {/* Customs clearance */}
            {state.currentStep >= CUSTOMS_CLEARANCE.DISPLAY_FROM_STEP && (
              <List.Item
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Space>
                  <Text>
                    {t(
                      "bookingWizard.pricingCalculator.customsClearance",
                      "Customs clearance"
                    )}
                  </Text>
                  <Tooltip
                    title={t(
                      "bookingWizard.pricingCalculator.customsClearanceTooltip",
                      `Base fee of €${CUSTOMS_CLEARANCE.BASE_FEE} includes up to ${CUSTOMS_CLEARANCE.INCLUDED_HS_CODES} unique HS codes. Each additional HS code costs €${CUSTOMS_CLEARANCE.EXTRA_HS_CODE_FEE}.`
                    )}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <Text>{formatEuro(calculatedPrices.customsClearance)}</Text>
              </List.Item>
            )}

            {/* Handling fees */}
            {state.currentStep >= HANDLING.DISPLAY_FROM_STEP && (
              <List.Item
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Space>
                  <Text>
                    {t(
                      "bookingWizard.pricingCalculator.handlingFees",
                      "Handling fees"
                    )}
                  </Text>
                  <Tooltip
                    title={t(
                      "bookingWizard.pricingCalculator.handlingFeesTooltip",
                      "Fees for handling the shipment at warehouses"
                    )}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <Text>{formatEuro(calculatedPrices.handlingFees)}</Text>
              </List.Item>
            )}
          </List>
        </div>
      )}

      {/* ===== DELIVERY COSTS SECTION ===== */}
      {state.currentStep >= DELIVERY.DISPLAY_FROM_STEP && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {t(
              "bookingWizard.pricingCalculator.deliveryCostsSectionTitle",
              "Delivery"
            )}
          </Text>
          <List size="small" itemLayout="horizontal" style={{ marginTop: 8 }}>
            {/* Diesel surcharge */}
            {state.currentStep >= AIR_FREIGHT.DISPLAY_FROM_STEP &&
              calculatedPrices.dieselSurcharge > 0 && (
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Space>
                    <Text>
                      {t(
                        "bookingWizard.pricingCalculator.dieselSurcharge",
                        "Diesel surcharge"
                      )}
                    </Text>
                    <Tooltip
                      title={t(
                        "bookingWizard.pricingCalculator.dieselSurchargeTooltip",
                        "Additional fee to account for fuel costs"
                      )}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                  <Text>{formatEuro(calculatedPrices.dieselSurcharge)}</Text>
                </List.Item>
              )}

            {/* Delivery cost */}
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Space>
                <Text>
                  {t(
                    "bookingWizard.pricingCalculator.deliveryCost",
                    "Delivery cost"
                  )}
                </Text>
                <Tooltip
                  title={t(
                    "bookingWizard.pricingCalculator.deliveryCostTooltip",
                    "Cost of delivering goods to the final destination"
                  )}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              <Text>{formatEuro(calculatedPrices.deliveryCost)}</Text>
            </List.Item>
          </List>
        </div>
      )}

      <Divider />

      {/* ===== TOTAL COST ===== */}
      <List size="small" itemLayout="horizontal">
        <List.Item style={{ display: "flex", justifyContent: "space-between" }}>
          <Title level={5}>
            {t("bookingWizard.pricingCalculator.total", "Total")}
          </Title>
          <Title level={5} style={{ color: "#3f8600" }}>
            {formatEuro(calculatedPrices.totalCost)}
          </Title>
        </List.Item>
      </List>

      <Divider />

      {/* Import duties note */}
      {state.currentStep >= IMPORT_DUTIES_NOTE.DISPLAY_FROM_STEP && (
        <Text
          type="warning"
          style={{
            fontSize: "12px",
            color: "#fa8c16", // Orange color that matches the warning icon
          }}
        >
          {t(
            "bookingWizard.pricingCalculator.importDutiesNote",
            "Note: Import duties, VAT, and other potential fees are not included in this calculation. These will be charged separately upon customs clearance."
          )}
        </Text>
      )}

      {/* Pickup Fee Modals */}
      {/* Pickup Fee Request Modal */}
      <Modal
        title={t("requestPickupFeeQuote", "Request Pickup Fee Quote")}
        open={isPickupRequestModalVisible}
        onOk={handlePickupFeeRequest}
        onCancel={() => setIsPickupRequestModalVisible(false)}
        confirmLoading={isRequestingPickup}
        okText={t("submitRequest", "Submit Request")}
        cancelText={t("cancel", "Cancel")}
        width={600}
      >
        <Typography.Paragraph>
          {t(
            "pickupRequestDescription",
            "Our team will review your request and provide a quote for pickup service from your supplier. You will be notified once the quote is ready."
          )}
        </Typography.Paragraph>

        <Divider orientation="left">
          {t("requestDetails", "Request Details")}
        </Divider>

        {/* Supplier Information */}
        <Card
          size="small"
          title={t("supplierInformation", "Supplier Information")}
          style={{ marginBottom: 16 }}
        >
          {bookingSupplier ? (
            <List size="small">
              <List.Item>
                <Text strong>{t("supplierName")}:</Text>
                <Text>{bookingSupplier.name}</Text>
              </List.Item>
              <List.Item>
                <Text strong>{t("location")}:</Text>
                <Text>{`${bookingSupplier.city}, ${bookingSupplier.country}`}</Text>
              </List.Item>
              <List.Item>
                <Text strong>{t("address")}:</Text>
                <Text>{bookingSupplier.address}</Text>
              </List.Item>
            </List>
          ) : (
            <Empty
              description={t(
                "noSupplierInformation",
                "No supplier information available"
              )}
            />
          )}
        </Card>

        {/* Cargo Information */}
        <Card
          size="small"
          title={t("cargoInformation", "Cargo Information")}
          style={{ marginBottom: 16 }}
        >
          <List size="small">
            <List.Item>
              <Text strong>{t("totalWeight")}:</Text>
              <Text>
                {formatWeight(
                  state.products?.reduce(
                    (sum, product) => sum + (product.gross_weight || 0),
                    0
                  ) || 0
                )}
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalVolume")}:</Text>
              <Text>
                {state.products
                  ?.reduce((sum, product) => sum + (product.cbm || 0), 0)
                  ?.toFixed(2)}{" "}
                m³
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalCartons")}:</Text>
              <Text>
                {state.products?.reduce(
                  (sum, product) => sum + (product.cartons || 0),
                  0
                ) || 0}
              </Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("totalPieces")}:</Text>
              <Text>
                {state.products?.reduce(
                  (sum, product) => sum + (product.pieces || 0),
                  0
                ) || 0}
              </Text>
            </List.Item>
          </List>
        </Card>

        {/* Transport Details */}
        <Card size="small" title={t("transportDetails", "Transport Details")}>
          <List size="small">
            <List.Item>
              <Text strong>{t("transportType")}:</Text>
              <Text>{transportType}</Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("incoterm")}:</Text>
              <Text>{incoterm}</Text>
            </List.Item>
            <List.Item>
              <Text strong>{t("cargoReadinessDate")}:</Text>
              <Text>
                {state.origin_pickup_date
                  ? dayjs(state.origin_pickup_date).format("DD/MM/YYYY")
                  : t("notSpecified")}
              </Text>
            </List.Item>
          </List>
        </Card>

        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
          {t(
            "pickupRequestNote",
            "Note: This request is specific to your current booking and supplier location."
          )}
        </Typography.Paragraph>
      </Modal>

      {/* Pickup Request Details Modal */}
      <Modal
        title={t("pickupRequestDetails", "Pickup Request Details")}
        open={isPickupDetailsModalVisible}
        onCancel={() => setIsPickupDetailsModalVisible(false)}
        footer={
          pickupRequest?.status === "offer"
            ? [
                <Button key="request_new" onClick={requestNewPickupFeeQuote}>
                  {t("requestNewQuote", "Request New Quote")}
                </Button>,
                <Button
                  key="cancel"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("cancel", "Cancel")}
                </Button>,
                <Button
                  key="accept"
                  type="primary"
                  onClick={acceptPickupFeeOffer}
                >
                  {t("acceptOffer", "Accept Offer")}
                </Button>,
              ]
            : pickupRequest?.status === "accepted"
            ? [
                <Button key="request_new" onClick={requestNewPickupFeeQuote}>
                  {t("requestNewQuote", "Request New Quote")}
                </Button>,
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("close", "Close")}
                </Button>,
              ]
            : [
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setIsPickupDetailsModalVisible(false)}
                >
                  {t("close", "Close")}
                </Button>,
              ]
        }
        width={600}
      >
        {pickupRequest && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Badge
                status={getStatusColor(pickupRequest.status)}
                text={
                  <Typography.Text strong style={{ fontSize: "16px" }}>
                    {getStatusText(pickupRequest.status)}
                  </Typography.Text>
                }
              />
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginTop: 8 }}
              >
                {t("requestedOn", "Requested on")}:{" "}
                {dayjs(pickupRequest.created_at).format("DD/MM/YYYY HH:mm")}
              </Typography.Text>

              {pickupRequest.offer_at && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 4 }}
                >
                  {t("offerProvidedOn", "Offer provided on")}:{" "}
                  {dayjs(pickupRequest.offer_at).format("DD/MM/YYYY HH:mm")}
                </Typography.Text>
              )}
            </div>

            {pickupRequest.status === "offer" && pickupRequest.cost && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f6ffed",
                  borderColor: "#b7eb8f",
                }}
              >
                <Typography.Title
                  level={5}
                  style={{ color: "#52c41a", margin: 0 }}
                >
                  {t("pickupOfferPrice", "Offered Price")}:{" "}
                  {formatEuro(pickupRequest.cost)}
                </Typography.Title>
              </Card>
            )}

            {pickupRequest.status === "accepted" && pickupRequest.cost && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f0f5ff",
                  borderColor: "#adc6ff",
                }}
              >
                <Typography.Title
                  level={5}
                  style={{ color: "#1890ff", margin: 0 }}
                >
                  {t("acceptedPickupFee", "Accepted Pickup Fee")}:{" "}
                  {formatEuro(pickupRequest.cost)}
                </Typography.Title>
              </Card>
            )}

            {pickupRequest.notes && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f5f5f5",
                  borderColor: "#d9d9d9",
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t("notes", "Notes")}
                </Typography.Title>
                <Typography.Paragraph>
                  {pickupRequest.notes}
                </Typography.Paragraph>
              </Card>
            )}

            <Divider orientation="left">
              {t("requestDetails", "Request Details")}
            </Divider>

            {/* Supplier Information */}
            <Card
              size="small"
              title={t("supplierInformation", "Supplier Information")}
              style={{ marginBottom: 16 }}
            >
              {pickupRequest.booking_blob.supplier ? (
                <List size="small">
                  <List.Item>
                    <Text strong>{t("supplierName")}:</Text>
                    <Text>{pickupRequest.booking_blob.supplier.name}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("location")}:</Text>
                    <Text>{`${pickupRequest.booking_blob.supplier.city}, ${pickupRequest.booking_blob.supplier.country}`}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("address")}:</Text>
                    <Text>{pickupRequest.booking_blob.supplier.address}</Text>
                  </List.Item>
                </List>
              ) : (
                <Empty
                  description={t(
                    "noSupplierInformation",
                    "No supplier information available"
                  )}
                />
              )}
            </Card>

            {/* Cargo Information */}
            <Card
              size="small"
              title={t("cargoInformation", "Cargo Information")}
              style={{ marginBottom: 16 }}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("totalWeight")}:</Text>
                  <Text>
                    {formatWeight(
                      pickupRequest.booking_blob.products_summary
                        .total_weight || 0
                    )}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalVolume")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary.total_cbm?.toFixed(
                      2
                    ) || "0.00"}{" "}
                    m³
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalCartons")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary
                      .total_cartons || 0}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalPieces")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.products_summary.total_pieces ||
                      0}
                  </Text>
                </List.Item>
              </List>
            </Card>

            {/* Transport Details */}
            <Card
              size="small"
              title={t("transportDetails", "Transport Details")}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("transportType")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info.transport_type}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("incoterm")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info.incoterm}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("cargoReadinessDate")}:</Text>
                  <Text>
                    {pickupRequest.booking_blob.transport_info
                      .origin_pickup_date
                      ? dayjs(
                          pickupRequest.booking_blob.transport_info
                            .origin_pickup_date
                        ).format("DD/MM/YYYY")
                      : t("notSpecified")}
                  </Text>
                </List.Item>
              </List>
            </Card>

            {/* Add Request History Section in the modal */}
            {pickupRequestHistory.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left">
                  {t("requestHistorySummary", "Request History")}
                </Divider>

                <List
                  itemLayout="horizontal"
                  dataSource={pickupRequestHistory.slice(1)} // Skip the first one which is current
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button
                          key="view_details"
                          type="link"
                          onClick={() => showHistoryRequestDetails(item)}
                        >
                          {t("viewDetails", "View Details")}
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Badge status={getStatusColor(item.status)} />}
                        title={
                          <Space>
                            <span>{getStatusText(item.status)}</span>
                            {item.cost && (
                              <Text type="success">
                                {formatEuro(item.cost)}
                              </Text>
                            )}
                          </Space>
                        }
                        description={dayjs(item.created_at).format(
                          "DD/MM/YYYY HH:mm"
                        )}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Historical Pickup Request Details Modal */}
      <Modal
        title={t("historicalRequestDetails", "Historical Request Details")}
        open={isHistoryDetailsModalVisible}
        onCancel={() => setIsHistoryDetailsModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setIsHistoryDetailsModalVisible(false)}
          >
            {t("close", "Close")}
          </Button>,
        ]}
        width={600}
      >
        {selectedHistoryRequest && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Badge
                status={getStatusColor(selectedHistoryRequest.status)}
                text={
                  <Typography.Text strong style={{ fontSize: "16px" }}>
                    {getStatusText(selectedHistoryRequest.status)}
                  </Typography.Text>
                }
              />
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginTop: 8 }}
              >
                {t("requestedOn", "Requested on")}:{" "}
                {dayjs(selectedHistoryRequest.created_at).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </Typography.Text>

              {selectedHistoryRequest.offer_at && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 4 }}
                >
                  {t("offerProvidedOn", "Offer provided on")}:{" "}
                  {dayjs(selectedHistoryRequest.offer_at).format(
                    "DD/MM/YYYY HH:mm"
                  )}
                </Typography.Text>
              )}
            </div>

            {selectedHistoryRequest.status === "offer" &&
              selectedHistoryRequest.cost && (
                <Card
                  style={{
                    marginBottom: 16,
                    backgroundColor: "#f6ffed",
                    borderColor: "#b7eb8f",
                  }}
                >
                  <Typography.Title
                    level={5}
                    style={{ color: "#52c41a", margin: 0 }}
                  >
                    {t("pickupOfferPrice", "Offered Price")}:{" "}
                    {formatEuro(selectedHistoryRequest.cost)}
                  </Typography.Title>
                </Card>
              )}

            {selectedHistoryRequest.notes && (
              <Card
                style={{
                  marginBottom: 16,
                  backgroundColor: "#f5f5f5",
                  borderColor: "#d9d9d9",
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t("notes", "Notes")}
                </Typography.Title>
                <Typography.Paragraph>
                  {selectedHistoryRequest.notes}
                </Typography.Paragraph>
              </Card>
            )}

            <Divider orientation="left">
              {t("requestDetails", "Request Details")}
            </Divider>

            {/* Supplier Information */}
            <Card
              size="small"
              title={t("supplierInformation", "Supplier Information")}
              style={{ marginBottom: 16 }}
            >
              {selectedHistoryRequest.booking_blob.supplier ? (
                <List size="small">
                  <List.Item>
                    <Text strong>{t("supplierName")}:</Text>
                    <Text>
                      {selectedHistoryRequest.booking_blob.supplier.name}
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("location")}:</Text>
                    <Text>{`${selectedHistoryRequest.booking_blob.supplier.city}, ${selectedHistoryRequest.booking_blob.supplier.country}`}</Text>
                  </List.Item>
                  <List.Item>
                    <Text strong>{t("address")}:</Text>
                    <Text>
                      {selectedHistoryRequest.booking_blob.supplier.address}
                    </Text>
                  </List.Item>
                </List>
              ) : (
                <Empty
                  description={t(
                    "noSupplierInformation",
                    "No supplier information available"
                  )}
                />
              )}
            </Card>

            {/* Cargo Information */}
            <Card
              size="small"
              title={t("cargoInformation", "Cargo Information")}
              style={{ marginBottom: 16 }}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("totalWeight")}:</Text>
                  <Text>
                    {formatWeight(
                      selectedHistoryRequest.booking_blob.products_summary
                        .total_weight || 0
                    )}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalVolume")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary.total_cbm?.toFixed(
                      2
                    ) || "0.00"}{" "}
                    m³
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalCartons")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary
                      .total_cartons || 0}
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("totalPieces")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.products_summary
                      .total_pieces || 0}
                  </Text>
                </List.Item>
              </List>
            </Card>

            {/* Transport Details */}
            <Card
              size="small"
              title={t("transportDetails", "Transport Details")}
            >
              <List size="small">
                <List.Item>
                  <Text strong>{t("transportType")}:</Text>
                  <Text>
                    {
                      selectedHistoryRequest.booking_blob.transport_info
                        .transport_type
                    }
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("incoterm")}:</Text>
                  <Text>
                    {
                      selectedHistoryRequest.booking_blob.transport_info
                        .incoterm
                    }
                  </Text>
                </List.Item>
                <List.Item>
                  <Text strong>{t("cargoReadinessDate")}:</Text>
                  <Text>
                    {selectedHistoryRequest.booking_blob.transport_info
                      .origin_pickup_date
                      ? dayjs(
                          selectedHistoryRequest.booking_blob.transport_info
                            .origin_pickup_date
                        ).format("DD/MM/YYYY")
                      : t("notSpecified")}
                  </Text>
                </List.Item>
              </List>
            </Card>
          </>
        )}
      </Modal>
    </Card>
  );
};
